<script setup lang="ts">
type Variant =
  | "default"
  | "primary"
  | "success"
  | "destructive"
  | "warning"
  | "plum"
  | "pine"
  | "ochre"
  | "indigo"
  | "moss";
type Size = "sm" | "md" | "lg";

withDefaults(
  defineProps<{
    variant?: Variant;
    size?: Size;
  }>(),
  {
    variant: "default",
    size: "sm",
  },
);
</script>

<template>
  <span
    class="badge"
    :class="[`badge--${variant}`, `badge--${size}`]"
  >
    <span
      v-if="$slots.leading"
      class="badge__leading"
    >
      <slot name="leading" />
    </span>
    <span class="badge__label"><slot /></span>
  </span>
</template>

<style scoped lang="scss">
// !important on font-size + line-height: the global `b,p,span,li,label`
// rule in main.scss has `span span { font-size: 1rem !important }` which
// otherwise wins over our scoped style on .badge__label.
// !important on margin: the global `b, p, span, li, label` rule in main.scss
// applies `margin: 10px 0` to every span — including this badge and its label.
// Without the override, every wrapped cuisine pill row inherits ~20px of
// vertical margin and pushes the rating row off the bottom of maker cards.
.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0 !important;
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  font-family: "GalanoGrotesqueMedium", sans-serif;
  font-weight: var(--weight-semibold);
  line-height: 1 !important;
  white-space: nowrap;
  border: 0.0625rem solid transparent;

  &,
  .badge__label {
    font-size: 0.6rem !important;
    line-height: 1 !important;
    margin: 0 !important;
  }

  &--sm {
    height: 0.875rem;
    padding: 0 0.3125rem;
    letter-spacing: 0.02em;
    &,
    .badge__label {
      font-size: 0.55rem !important;
    }
  }
  &--md {
    height: 1.125rem;
    &,
    .badge__label {
      font-size: 0.625rem !important;
    }
  }
  &--lg {
    height: 1.15rem;
    padding: 0 0.5625rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    &,
    .badge__label {
      font-size: 0.625rem !important;
    }
  }

  &__leading {
    display: inline-flex;
    align-items: center;
  }

  &--default {
    background-color: var(--color-grey-4);
    color: var(--color-grey-1);
    border-color: var(--color-grey-3);
  }
  &--primary {
    background-color: var(--color-pink-4);
    color: var(--color-pink-1);
    border-color: var(--color-pink-1);
  }
  &--success {
    background-color: var(--color-green-3);
    color: var(--color-green-2);
    border-color: var(--color-green-2);
  }
  &--destructive {
    background-color: var(--color-pink-3);
    color: var(--color-red-1);
    border-color: var(--color-red-1);
  }
  &--warning {
    background-color: var(--color-yellow-2);
    color: var(--color-orange-1);
    border-color: var(--color-orange-1);
  }
  &--plum {
    background-color: var(--color-plum-2);
    color: var(--color-plum-1);
  }
  &--pine {
    background-color: var(--color-pine-2);
    color: var(--color-pine-1);
  }
  &--ochre {
    background-color: var(--color-ochre-2);
    color: var(--color-ochre-1);
  }
  &--indigo {
    background-color: var(--color-indigo-2);
    color: var(--color-indigo-1);
  }
  &--moss {
    background-color: var(--color-moss-2);
    color: var(--color-moss-1);
  }
}
</style>
